import * as Sentry from "@sentry/react";
import AppRoot from "app/AppRoot";
import { AppRootRouteErrorPage } from "app/AppRootRouteErrorPage";
import { accountRoutes } from "features/account/AccountRoutes";
import { adminRoutes } from "features/admin/AdminRoutes";
import { auditingRoutes } from "features/auditing/AuditingRoutes";
import { AuthRequired } from "features/auth/AuthRequired";
import { authRoutes } from "features/auth/AuthRoutes";
import { createEntityEditRoutes } from "features/entities/edit/EntityEditRoutes";
import fundFamilyRoutes from "features/fund-family/FundFamilyRoutes";
import { internalRoutes } from "features/internal/InternalRoutes";
import { createInvestorEntityRoutes } from "features/investor-entities/InvestorEntityRoutes";
import { investorRoutes } from "features/investors/InvestorRoutes";
import reportRoutes from "features/reports/ReportRoutes";
import { settingsRoutes } from "features/settings/SettingRoutes";
import { getContextFeatureFlagRetrieveLoader } from "gen/endpoints/feature-flags/feature-flags";
import {
  getBootstrapLoader,
  getPingLoader,
} from "gen/endpoints/frontend/frontend";
import { getFundFamilyListLoader } from "gen/endpoints/fund-family/fund-family";
import { getGlobalEntityRetrieveLoader } from "gen/endpoints/global-entity/global-entity";
import { getGlobalEntityTableDataRetrieveLoader } from "gen/endpoints/global-entity-table-data/global-entity-table-data";
import {
  getUserCustomerListLoader,
  getUsersMeRetrieveLoader,
} from "gen/endpoints/users/users";
import { createBrowserRouter, Navigate } from "react-router-dom";
import NotFoundPage from "shared/components/empty/NotFoundPage";
import { isDev } from "shared/utils/DevUtils";
import { composeLoaders } from "shared/utils/RoutingUtils";

const browserRouterCreator = isDev
  ? createBrowserRouter
  : Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const appRouter = browserRouterCreator([
  {
    id: "root",
    element: <AppRoot />,
    ErrorBoundary: AppRootRouteErrorPage,
    children: [
      authRoutes,
      {
        id: "protectedRoutes",
        element: <AuthRequired />,
        loader: composeLoaders(
          getBootstrapLoader(),
          getPingLoader(),
          getFundFamilyListLoader(),
          getContextFeatureFlagRetrieveLoader(),
          getUsersMeRetrieveLoader(),
          getUserCustomerListLoader(),
        ),
        children: [
          {
            id: "dashboard",
            path: "/",
            element: <Navigate to="/fund-family" replace={true} />,
          },
          accountRoutes,
          adminRoutes,
          auditingRoutes,
          fundFamilyRoutes,
          reportRoutes,
          settingsRoutes,
          {
            id: "entities",
            path: "entities",
            children: [
              {
                id: "entitiesList",
                index: true,
                lazy: async () => {
                  const { EntitiesListPage } = await import(
                    "features/entities/EntitiesListPage"
                  );
                  return { Component: EntitiesListPage };
                },
                loader: getGlobalEntityTableDataRetrieveLoader(),
              },
              {
                id: "entitiesBulkAddBanks",
                path: "bulk-add-banks",
                lazy: async () => {
                  const { EntitiesBulkAddBanksPage } = await import(
                    "features/entities/EntitiesBulkAddBanksPage"
                  );
                  return { Component: EntitiesBulkAddBanksPage };
                },
              },
              {
                id: "entitiesBulkAddContacts",
                path: "bulk-add-contacts",
                lazy: async () => {
                  const { EntitiesBulkAddContactsPage } = await import(
                    "features/entities/EntitiesBulkAddContactsPage"
                  );
                  return { Component: EntitiesBulkAddContactsPage };
                },
              },
              {
                id: "entitiesEntity",
                path: ":entityId",
                lazy: async () => {
                  const { EntityEditPage } = await import(
                    "features/entities/edit/EntityEditPage"
                  );
                  return {
                    Component: EntityEditPage,
                  };
                },
                loader: getGlobalEntityRetrieveLoader(({ entityId }) => ({
                  id: entityId!,
                })),
                children: createEntityEditRoutes("global"),
              },
            ],
          },
          {
            id: "transactions",
            path: "transactions",
            lazy: async () => {
              const { TransactionsListPage } = await import(
                "features/transactions/TransactionsListPage"
              );
              return { Component: TransactionsListPage };
            },
          },
          createInvestorEntityRoutes(),
          internalRoutes,
          investorRoutes,
          {
            id: "protectedNotFound",
            path: "*",
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
  {
    id: "notFound",
    path: "*",
    element: <NotFoundPage />,
  },
]);
